(() => {
  'use strict';
  if (window.AWAirsoftContext?.version === '1.2.2') return;

  const CONTEXT_KEY = 'aw_airsoft_context_v1';
  const CONFIG_KEY = 'aw_airsoft_configurator';
  const LEGACY = {
    platform: 'aw_selected_platform',
    manufacturer: 'aw_selected_manufacturer',
    type: 'aw_selected_replica_type'
  };
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);

  const normalize = (value = '') => String(value).trim().toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const readJson = (key, fallback = {}) => {
    try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; } catch (_) { return fallback; }
  };
  const writeJson = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {} };
  const readText = (key) => { try { return localStorage.getItem(key) || ''; } catch (_) { return ''; } };
  const writeText = (key, value) => {
    try { if (value) localStorage.setItem(key, value); else localStorage.removeItem(key); } catch (_) {}
  };

  const legacyContext = () => ({
    platform: normalize(readText(LEGACY.platform)),
    manufacturer: normalize(readText(LEGACY.manufacturer)),
    type: normalize(readText(LEGACY.type))
  });

  const current = () => {
    const stored = readJson(CONTEXT_KEY, {});
    const legacy = legacyContext();
    const storedBase = {
      platform: normalize(stored.platform || ''),
      manufacturer: normalize(stored.manufacturer || ''),
      type: normalize(stored.type || '')
    };
    const legacyPlatformActive = Boolean(legacy.platform);
    const platform = normalize(legacy.platform || storedBase.platform);
    const manufacturer = legacyPlatformActive
      ? normalize(legacy.manufacturer)
      : normalize(legacy.manufacturer || storedBase.manufacturer);
    const type = legacyPlatformActive
      ? normalize(legacy.type)
      : normalize(legacy.type || storedBase.type);
    const baseMismatch = storedBase.platform !== platform
      || storedBase.manufacturer !== manufacturer
      || storedBase.type !== type;
    return {
      version: 1,
      platform,
      platformLabel: !baseMismatch && storedBase.platform === platform ? String(stored.platformLabel || '') : '',
      manufacturer,
      manufacturerLabel: !baseMismatch && storedBase.manufacturer === manufacturer ? String(stored.manufacturerLabel || '') : '',
      type,
      typeLabel: !baseMismatch && storedBase.type === type ? String(stored.typeLabel || '') : '',
      model: baseMismatch ? '' : String(stored.model || ''),
      modelLabel: baseMismatch ? '' : String(stored.modelLabel || ''),
      modelHandle: baseMismatch ? '' : String(stored.modelHandle || ''),
      source: baseMismatch ? 'legacy-reconciled' : String(stored.source || 'legacy'),
      updatedAt: String(stored.updatedAt || '')
    };
  };

  const relationGuard = () => {
    const guard = window.AWConfiguratorRelationGuard;
    return guard?.version === '2.1.1' && guard?.evaluate && guard?.sanitizeCurrent ? guard : null;
  };

  const markDeferred = (root) => {
    if (!root) return { removed: 0, deferred: true };
    root.dataset.awContextSanitized = 'deferred-relational';
    root.dataset.awContextRemoved = '0';
    const state = readJson(root.dataset.storageKey || CONFIG_KEY, {});
    if (Array.isArray(state.selected) && state.selected.length) {
      state.contextNeedsReview = true;
      writeJson(root.dataset.storageKey || CONFIG_KEY, state);
    }
    return { removed: 0, deferred: true };
  };

  const removeReadySelection = (root) => {
    if (!root || root.dataset.awReady !== 'true') return markDeferred(root);
    const guard = relationGuard();
    if (!guard) return markDeferred(root);

    const before = readJson(root.dataset.storageKey || CONFIG_KEY, {});
    const beforeCount = Array.isArray(before.selected) ? before.selected.length : 0;
    const result = guard.sanitizeCurrent(root, false) || { removed: 0 };
    const after = readJson(root.dataset.storageKey || CONFIG_KEY, {});
    const afterCount = Array.isArray(after.selected) ? after.selected.length : beforeCount;
    const removed = Math.max(Number(result.removed || 0), beforeCount - afterCount, 0);

    root.dataset.awContextSanitized = 'relational';
    root.dataset.awContextRemoved = String(removed);
    if (after.contextNeedsReview) {
      after.contextNeedsReview = false;
      writeJson(root.dataset.storageKey || CONFIG_KEY, after);
    }

    if (removed > 0) {
      const notice = root.querySelector('[data-aw-config-notice]');
      if (notice) {
        notice.hidden = false;
        notice.textContent = `${removed} composant${removed > 1 ? 's bloquants ont' : ' bloquant a'} été retiré${removed > 1 ? 's' : ''} uniquement de la configuration courante selon Compatibility AW.`;
        notice.classList.add('is-error');
        notice.classList.remove('is-success');
      }
      window.dispatchEvent(new CustomEvent('aw:configuration-sanitized', {
        detail: { platform: current().platform, removed, policy: guard.policy || 'relational' }
      }));
    }
    return { removed, deferred: false };
  };

  const sanitizeStoredConfigurator = (root) => {
    if (!root) return { removed: 0, deferred: true };
    if (root.dataset.awReady === 'true') return removeReadySelection(root);
    return markDeferred(root);
  };

  const sanitizeAll = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-aw-configurator]')) roots.push(scope);
    scope.querySelectorAll?.('[data-aw-configurator]').forEach((root) => roots.push(root));
    return roots.map((root) => sanitizeStoredConfigurator(root));
  };

  const retrySanitize = (attempt = 0) => {
    const guard = relationGuard();
    const readyRoots = Array.from(document.querySelectorAll('[data-aw-configurator]')).filter((root) => root.dataset.awReady === 'true');
    if (guard && readyRoots.length) {
      sanitizeAll(document);
      return;
    }
    if (attempt < 40) setTimeout(() => retrySanitize(attempt + 1), 100);
  };

  const writeContext = (detail = {}, source = 'unknown') => {
    const previous = current();
    const explicitModel = own(detail, 'model') || own(detail, 'modelLabel') || own(detail, 'modelHandle');
    const platform = normalize(detail.platform ?? previous.platform);
    const manufacturer = normalize(detail.manufacturer ?? previous.manufacturer);
    const type = normalize(detail.replicaType ?? detail.type ?? previous.type);
    const baseChanged = platform !== normalize(previous.platform)
      || manufacturer !== normalize(previous.manufacturer)
      || type !== normalize(previous.type);
    const model = explicitModel ? String(detail.model ?? detail.modelLabel ?? '') : (baseChanged ? '' : String(previous.model || ''));
    const modelLabel = explicitModel ? String(detail.modelLabel ?? detail.model ?? '') : (baseChanged ? '' : String(previous.modelLabel || ''));
    const modelHandle = own(detail, 'modelHandle')
      ? String(detail.modelHandle || '')
      : (explicitModel || baseChanged ? '' : String(previous.modelHandle || ''));

    const next = {
      version: 1,
      platform,
      platformLabel: String(own(detail, 'platformLabel') ? (detail.platformLabel || '') : (baseChanged ? '' : previous.platformLabel || '')),
      manufacturer,
      manufacturerLabel: String(own(detail, 'manufacturerLabel') ? (detail.manufacturerLabel || '') : (baseChanged ? '' : previous.manufacturerLabel || '')),
      type,
      typeLabel: String((own(detail, 'replicaTypeLabel') || own(detail, 'typeLabel'))
        ? (detail.replicaTypeLabel ?? detail.typeLabel ?? '')
        : (baseChanged ? '' : previous.typeLabel || '')),
      model,
      modelLabel,
      modelHandle,
      source,
      updatedAt: new Date().toISOString()
    };

    writeJson(CONTEXT_KEY, next);
    writeText(LEGACY.platform, next.platform);
    writeText(LEGACY.manufacturer, next.manufacturer);
    writeText(LEGACY.type, next.type);

    const state = readJson(CONFIG_KEY, {});
    if (state && typeof state === 'object' && next.platform) {
      const platformChanged = normalize(state.platform) !== next.platform;
      const manufacturerChanged = normalize(state.manufacturer) !== next.manufacturer;
      const typeChanged = normalize(state.type) !== next.type;
      const stateModel = normalize(state.modelHandle || state.modelLabel || state.model || '');
      const nextModel = normalize(next.modelHandle || next.modelLabel || next.model || '');
      const modelChanged = stateModel !== nextModel;
      if (platformChanged || manufacturerChanged || typeChanged || modelChanged) {
        state.platform = next.platform;
        state.platformLabel = next.platformLabel || '';
        state.manufacturer = next.manufacturer;
        state.type = next.type;
        state.model = next.model;
        state.modelLabel = next.modelLabel;
        state.modelHandle = next.modelHandle;
        state.savedId = '';
        state.contextNeedsReview = Array.isArray(state.selected) && state.selected.length > 0
          && (platformChanged || manufacturerChanged || typeChanged || modelChanged);
        if (platformChanged && /^Configuration\s+/i.test(String(state.name || ''))) {
          state.name = `Configuration ${next.platformLabel || next.platform}`;
        }
        writeJson(CONFIG_KEY, state);
      }
    }

    window.dispatchEvent(new CustomEvent('aw:context-changed', { detail: next }));
    queueMicrotask(() => {
      sanitizeAll(document);
      retrySanitize();
    });
    return next;
  };

  const initial = current();
  writeJson(CONTEXT_KEY, { ...initial, version: 1, updatedAt: initial.updatedAt || new Date().toISOString() });
  writeText(LEGACY.platform, initial.platform);
  writeText(LEGACY.manufacturer, initial.manufacturer);
  writeText(LEGACY.type, initial.type);

  window.addEventListener('aw:platform-changed', (event) => writeContext(event.detail || {}, 'platform'));
  window.addEventListener('aw:compatibility-changed', (event) => writeContext(event.detail || {}, 'compatibility'));
  document.addEventListener('shopify:section:load', (event) => {
    sanitizeAll(event.target);
    retrySanitize();
  });

  new MutationObserver(() => {
    if (relationGuard()) sanitizeAll(document);
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['data-aw-ready'] });

  queueMicrotask(() => {
    sanitizeAll(document);
    retrySanitize();
  });

  window.AWAirsoftContext = {
    version: '1.2.2',
    key: CONTEXT_KEY,
    policy: 'relational-sanitizer-with-authoritative-legacy-platform-reconciliation',
    current,
    write: writeContext,
    sanitizeStoredConfigurator,
    sanitizeAll,
    removeReadySelection
  };
})();
